import React, { useState } from 'react';
import { LabelUppercase, P, PExtraSmall, PSmall } from '../../elements/Text';
import { TopicPill, TopicIcon, TopicText } from '../TopicPill';
import { MultiLineInput } from '../../elements/Input';
import Button from '../../elements/Button';
import styled, { useTheme } from 'styled-components';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { SmallTooltip } from '../../Tooltip';
import {
  SubmissionResponse,
  UNIT_TYPE,
  reviewSubmission,
} from '../../../api/admin';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { TOPIC_TO_COLOR } from '../../../theme';
import queryString from 'query-string';
import { useMutation } from 'react-query';
import useAuth from '../../../hooks/use-auth';
import { toast } from 'react-toastify';
import { useHistory, useParams } from 'react-router-dom';
import { useGetExerciseSubmissions } from '../../../hooks/use-hydra/admin';
import Answer from './Answer';
import SmallMarkdown from '../../Markdown/SmallMarkdown';
import { ROUTES } from '../../../routing';

dayjs.extend(utc);
dayjs.extend(timezone);

const Container = styled.div`
  padding: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SubHeader = styled(LabelUppercase)`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 14px;
  margin-top: 0;
  padding-bottom: 0;
  width: 100%;
`;

const QuotedText = styled(PSmall)`
  margin: 0;
  padding: 12px;
  color: ${({ theme }) => theme.colors.textRegular};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  font-style: italic;
`;
const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const MetadataWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex-wrap: wrap;
`;

const Pill = styled(TopicPill)<{ color?: string }>`
  padding: 4px 12px;
  border-radius: 999px;
`;
const PillText = styled(TopicText)`
  font-size: 16px;
  padding: 4px 0px;
`;

const CollapsibleHeader = styled.div<{ expanded: boolean }>`
  background-color: ${({ theme }) => theme.colors.inputBackground};
  width: 100%;
  padding: 20px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  gap: 16px;
  transition: all 0.3s ease-in-out;
  color: ${({ theme }) => theme.colors.inputTextActive};
  border-radius: 8px 8px ${({ expanded }) => (expanded ? '0 0' : '8px 8px')};

  &:hover {
    border-color: ${({ theme }) => theme.colors.inputBorderActive};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const ToggleIcon = styled.i`
  width: 20px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: transform 0.2s ease-in-out;
  transform: ${({ 'aria-expanded': expanded }) =>
    expanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
`;

type ReviewSubmissionProps = {
  submission: SubmissionResponse['submissions'][number];
};

type CollapsibleSectionProps = {
  title: React.ReactNode;
  children: React.ReactNode;
  isOpenByDefault?: boolean;
  style?: React.CSSProperties;
  className?: string;
  rightContent?: React.ReactNode;
};

const CollapsibleSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  border: 1px solid ${({ theme }) => theme.colors.inputBorderActive};
  border-radius: ${({ theme }) => theme.dimensions.sectionBorderRadius};
  overflow: hidden;
`;

export function CollapsibleSection({
  title,
  children,
  isOpenByDefault = false,
  style,
  className,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = React.useState(isOpenByDefault);
  const [animationParent] = useAutoAnimate();
  return (
    <CollapsibleSectionContainer style={style} className={className}>
      <CollapsibleHeader
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        expanded={isOpen}
      >
        <HeaderLeft>
          <ToggleIcon
            className={isOpen ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
            aria-expanded={isOpen}
          />
          <SubHeader>{title}</SubHeader>
        </HeaderLeft>
      </CollapsibleHeader>
      <div ref={animationParent}>{isOpen && children}</div>
    </CollapsibleSectionContainer>
  );
}

const ColumnsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: 16px;
`;

const Divider = styled.div`
  width: 1px;
  height: auto;
  background-color: ${({ theme }) => theme.colors.inputBorderActive};
`;

const Column = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export default function ReviewSubmission({
  submission,
}: ReviewSubmissionProps) {
  const [review, setReview] = useState(
    submission.submission.isReviewed ? submission.submission.mentorReview : ''
  );
  const theme = useTheme();
  const { getTokenSilently } = useAuth();
  const history = useHistory();
  const { id: companyId } = useParams<{ id: string }>();
  const { refetch: refetchSubmissions } = useGetExerciseSubmissions(
    parseInt(companyId)
  );
  const learnerName = submission?.learner.name;
  const learnerEmail = submission.learner.email;
  const requestedAt = submission.submission.requestedAt || new Date();
  const learnerComment =
    submission.submission.learnerComment || 'No comment provided.';
  const exerciseNumber = submission.question.index + 1;
  const lessonTitle = submission.unit.title;

  const requestedAtDate = dayjs(requestedAt).tz(dayjs.tz.guess());
  const fullTimestamp = requestedAtDate.format('DD MMM YYYY HH:mm:ss [GMT]Z');

  const topicColor =
    TOPIC_TO_COLOR[submission.unit.topic as keyof typeof TOPIC_TO_COLOR] ||
    theme.colors.primaryBg;
  const iconUrl = submission.unit.iconUrl;

  const onPreviewAnswers = () => {
    if (submission.unit.type === UNIT_TYPE.PROJECT) {
      const previewProjectUrl =
        window.location.origin +
        ROUTES.PREVIEW.PROJECT.getPath({
          projectSlug: submission.unit.projectSlug,
        });
      const previewProjectUrlWithQuery = `${previewProjectUrl}?${queryString.stringify(
        {
          stringifiedAnswerStates: JSON.stringify(
            submission.submission.fullAnswerStates
          ),
          qIndex: submission.question.index,
        }
      )}`;
      window.open(previewProjectUrlWithQuery, '_blank');
    }
    if (submission.unit.type === UNIT_TYPE.ACTIVITY) {
      const previewActivityUrl =
        window.location.origin +
        ROUTES.PREVIEW.ACTIVITY.getPath({
          missionSlug: submission.unit.missionSlug,
          activitySlug: submission.unit.activitySlug,
        });
      const previewActivityUrlWithQuery = `${previewActivityUrl}?${queryString.stringify(
        {
          stringifiedAnswerStates: JSON.stringify(
            submission.submission.fullAnswerStates
          ),
          qIndex: submission.question.index,
        }
      )}`;
      window.open(previewActivityUrlWithQuery, '_blank');
    }
  };

  const submitReviewMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();
      const submissionId = submission.submission.id;

      await reviewSubmission(token, {
        companyId: parseInt(companyId),
        submissionId,
        review,
        type: submission.unit.type,
        questionId: submission.question.id,
      });
    },
    onSuccess: async () => {
      toast.success('Review submitted successfully');
      await refetchSubmissions();
      history.push(ROUTES.ADMIN.SOS_QUESTIONS.getPath({ companyId }));
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Failed to submit review'
      );
    },
  });

  const handleSubmitReview = () => {
    submitReviewMutation.mutate();
  };

  return (
    <Container>
      <P>
        <span className="learner-label">
          <b>{learnerName}</b>
        </span>{' '}
        requested help for the <b>{lessonTitle}</b> lesson.
        <SmallTooltip anchorSelect=".learner-label">
          {learnerEmail}
        </SmallTooltip>
      </P>
      <MetadataWrapper>
        <Pill backgroundColor={topicColor}>
          {iconUrl && <TopicIcon src={iconUrl} alt="icon" />}
          <PillText>{submission.unit.topic}</PillText>
        </Pill>
        <Pill
          backgroundColor={theme.colors.bgActive}
          color={theme.colors.textPrimary}
        >
          <PillText>Exercise #{exerciseNumber}</PillText>
        </Pill>

        <Pill
          backgroundColor={theme.colors.bgActive}
          color={theme.colors.textPrimary}
          className="requested-date-pill"
        >
          <PillText>Requested {requestedAtDate.fromNow()}</PillText>
        </Pill>
        <SmallTooltip anchorSelect=".requested-date-pill">
          {fullTimestamp}
        </SmallTooltip>
      </MetadataWrapper>

      <CollapsibleSection title="Learner's Comment" isOpenByDefault>
        <QuotedText>{learnerComment}</QuotedText>
      </CollapsibleSection>
      <SubHeader style={{ marginTop: '32px' }}>Mentor Reply</SubHeader>
      <PSmall style={{ marginTop: '4px' }}>
        We'll notify the learner and they'll be able to see your reply within
        the learning platform. <br />
        We recommend using the <b>Preview learner answers</b> feature to
        understand the context of the exercise.
      </PSmall>
      <MultiLineInput
        style={{ marginTop: '0px', paddingBottom: '0px' }}
        placeholder="Reply to the learner's comment here"
        value={review}
        onChange={(e) => setReview(e.target.value)}
        maxRows={5}
      />
      <PExtraSmall
        style={{
          marginTop: 0,
          fontWeight: 400,
          color: theme.colors.inputTextPlaceholder,
        }}
      >
        PS: Press Enter to add a new line
      </PExtraSmall>
      <ButtonContainer>
        <Button
          label="Preview learner answers"
          type="secondary"
          color={theme.colors.buttonSecondary}
          showIconBeforeLabel
          faIcon="fa-duotone fa-eye"
          onClick={onPreviewAnswers}
        />
        <Button
          label={
            submission.submission.isReviewed ? 'Update reply' : 'Submit reply'
          }
          type="primary"
          showIconBeforeLabel
          faIcon="fa-duotone fa-paper-plane"
          onClick={handleSubmitReview}
          isDisabled={!review}
          isLoading={submitReviewMutation.isLoading}
        />
      </ButtonContainer>
      <CollapsibleSection title="Exercise" isOpenByDefault={false}>
        <ColumnsContainer>
          <SmallMarkdown md={submission.question.text} />
        </ColumnsContainer>
      </CollapsibleSection>
      <CollapsibleSection
        title="Learner vs Model Answer"
        isOpenByDefault={false}
      >
        <ColumnsContainer>
          <Column style={{ width: '46.5%' }}>
            <SubHeader>Learner's Answer</SubHeader>
            <Answer
              md={submission.submission.learnerAnswer}
              questionType={submission.question.type}
            />
            {submission.question.aiReview && (
              <>
                <SubHeader>AI Review</SubHeader>
                <SmallMarkdown md={submission.question.aiReview} />
              </>
            )}
          </Column>
          <Divider />
          <Column style={{ width: '46.5%' }}>
            <SubHeader>Model Answer</SubHeader>
            <Answer
              md={submission.question.modelAnswer}
              questionType={submission.question.type}
            />
          </Column>
        </ColumnsContainer>
      </CollapsibleSection>
      <CollapsibleSection title="Tips and notes" isOpenByDefault={false}>
        <ColumnsContainer>
          <SmallMarkdown md={submission.question.mentorNotes} />
        </ColumnsContainer>
      </CollapsibleSection>
    </Container>
  );
}
